import {batch} from 'react-redux';
import {FmlxRadioButton} from 'fmlx-common-ui';

import ErrorBarcodeModalContent from '../../components/ErrorBarcode/ErrorBarcodeModalContent';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import MessageBox, {getMessageBoxActionButtons} from '../../components/MessageBox';

import {
  BarcodeStatus,
  FastRobotErrorcode,
  LastExecEnum,
  RuntimeMode,
  Feedback,
  TrayType,
  UserRoleLevel,
  CommonButtonLabel,
  RuntimeType,
} from '../../constants/dataEnum';
import {showLoading} from '../actions/appStateAction';
import {setLabwareState} from '../actions/labwareStateAction';
import {hideModal, setTimerInterval, showModal} from '../actions/modalStateAction';
import {
  setAbortParam,
  setExecutionCount,
  setRuntimePauseRemainingTime,
  showRuntimeDialog,
} from '../actions/runtimeStateAction';
import {resolveIntl} from './commonBusiness';
import {abortActionBatch, pauseProtocol, resumeProtocol} from './instrumentBusiness';
import {saveProtocol, updateLabwareCurrentRunProtocol} from './protocolManagementBusiness';
import {getPicklistLabwareIDs} from './taskListPicklistBusiness';
import {isProtocolExecutor} from './stateBusiness';
import {addLeadingZeros, convertSecondToHoursMinuteSecond} from '../../lib/fastCommonModule';
import {setPrevErrorCodeSetTipModal, setShowSetTipModal} from '../actions/tipStateAction';
import {addTemporaryState} from '../actions/temporaryStateAction';
import {convertCardStateToCsv} from './protocolStateConversionBusiness';
import {showFormattedModal, showModalSimplified} from './commonModalBusiness';
import {isRoleLevel} from './userManagementBusiness';
import {HintType} from '../../constants/UIComponentEnum';
import {parseMessage} from '../../lib/stringHelper';
import {openTipsPickupCalibrationMenu} from './menuBusiness';

const intl = resolveIntl();

const scanTips = () => async (dispatch) => {
  dispatch(
    showLoading(
      intl.formatMessage({
        id: 'runtimeBusiness.scanTip.loading',
      }),
    ),
  );

  dispatch(resumeProtocol(false, false, true));
};

const createAbortModalButtons =
  (callback, usernameRequester = '') =>
  (dispatch, getState) => [
    {
      className: 'btn-lg',
      style: 'primary',
      handlerClick: async () => {
        dispatch(hideModal());

        if (callback) {
          callback();
          // eslint-disable-next-line no-param-reassign
          callback = null;
        } else {
          const {runtimeState} = getState();
          const {status, pausedOn} = runtimeState;
          if (
            status !== RuntimeMode.ERROR_ABORT &&
            status !== RuntimeMode.ERROR_ABORT_MOTOR_SKIP &&
            status !== RuntimeMode.ERROR_ABORT_MOTOR_COLLISION_SKIP &&
            status !== RuntimeMode.ERROR_MISSING_TRAY &&
            !pausedOn
          ) {
            await dispatch(resumeProtocol(false, false, true));
          }
        }
      },
      text: intl.formatMessage({id: CommonButtonLabel.CANCEL}),
    },
    {
      className: 'btn-lg',
      style: 'secondary',
      handlerClick: () => {
        dispatch(abortActionBatch(usernameRequester));
      },
      text: intl.formatMessage({
        id: 'runtime.abortModal.firstButton',
      }),
    },
  ];

export const abortPauseResponseHandler =
  (lastExecParam, callback = null, usernameRequester = '') =>
  async (dispatch, getState) => {
    const {runtimeState, userManagementState, sessionState} = getState();
    const {availableRoleRights} = userManagementState;
    const {abortParam, pausedOn} = runtimeState;
    let abortText;
    let lastExec = lastExecParam;
    if (!lastExecParam) lastExec = pausedOn;
    switch (lastExec) {
      case LastExecEnum.SCAN_STUCK_TIPS:
      case LastExecEnum.ALIGN_CHECK:
      case LastExecEnum.MIXING:
      case LastExecEnum.NONE:
      // eslint-disable-next-line default-case-last, no-fallthrough
      default: {
        abortText = (
          <div>
            {intl.formatMessage({
              id: 'runtime.abortModal.generalAbort',
            })}
          </div>
        );
        break;
      }
      case LastExecEnum.SCAN_TIPS: {
        abortText = (
          <div>
            {intl.formatMessage({
              id: 'runtime.abortModal.scanFail',
            })}
          </div>
        );
        break;
      }
      case LastExecEnum.ALIGN_TO_PICK_TIP:
      case LastExecEnum.PICK_TIP:
      case LastExecEnum.ALIGN_TO_ASPIRATE: {
        abortText = (
          <div>
            {intl.formatMessage({
              id: 'runtime.abortModal.beforeAspirate',
            })}
          </div>
        );
        break;
      }
      case LastExecEnum.ALIGN_TO_ASPIRATE_CARRY_LIQUID: {
        abortText = (
          <div>
            {intl.formatMessage({
              id: 'runtime.abortModal.beforeAspirateCarrryLiquid',
            })}
          </div>
        );
        break;
      }
      case LastExecEnum.ASPIRATE: {
        abortText = (
          <div>
            {intl.formatMessage({
              id: 'runtime.abortModal.aspirate',
            })}
          </div>
        );
        break;
      }
      case LastExecEnum.ENTER_WELL_BEFORE_ASPIRATE:
      case LastExecEnum.ASPIRATE_CARRY_LIQUID:
      case LastExecEnum.ALIGN_TO_DISPENSE_CARRY_LIQUIDS: {
        abortText = (
          <div>
            {intl.formatMessage({
              id: 'runtime.abortModal.afterAspirateCarrryLiquid',
            })}
          </div>
        );
        break;
      }
      case LastExecEnum.DISPENSE_LIQUID:
      case LastExecEnum.ALIGN_TO_DISPENSE: {
        abortText = (
          <div>
            {intl.formatMessage({id: 'runtime.abortModal.beforeDispense'})}
            <FmlxRadioButton
              title={intl.formatMessage({id: 'runtime.abortModal.beforeDispense.selectionLabel'})}
              selectedIndex={abortParam ? 0 : 1}
              onChange={(opt) => dispatch(setAbortParam(!(opt.index === 1)))}
              items={[
                {
                  content: intl?.formatMessage({id: 'runtime.abortModal.beforeDispense.optionOne'}),
                  hint: intl?.formatMessage({id: 'runtime.abortModal.beforeDispense.optionOne.hint'}),
                  type: HintType.INFORMATION,
                },
                {
                  content: intl?.formatMessage({id: 'runtime.abortModal.beforeDispense.optionTwo'}),
                  hint: intl?.formatMessage({id: 'runtime.abortModal.beforeDispense.optionTwo.hint'}),
                  type: HintType.INFORMATION,
                },
              ]}
            />
          </div>
        );
        break;
      }
      case LastExecEnum.DISPENSE_LIQUIDS: {
        abortText = (
          <div>
            {intl.formatMessage({
              id: 'runtime.abortModal.dispense',
            })}
          </div>
        );
        break;
      }
      case LastExecEnum.ALIGN_TO_ASPIRATE_REUSE_TIP:
      case LastExecEnum.ALIGN_TO_EJECT_TIP:
      case LastExecEnum.EJECT_TIP: {
        abortText = (
          <div>
            {intl.formatMessage({
              id: 'runtime.abortModal.ejectTip',
            })}
          </div>
        );
        break;
      }
      case LastExecEnum.ALIGN_TO_RETURN_TIP:
      case LastExecEnum.RETURN_TIP: {
        abortText = (
          <div>
            {intl.formatMessage({
              id: 'runtime.abortModal.ejectTipRearange',
            })}
          </div>
        );
        break;
      }
      case LastExecEnum.MIXING_AFTER_DISPENSE:
      case LastExecEnum.MIXING_BEFORE_ASPIRATE: {
        abortText = (
          <div>
            {intl.formatMessage({
              id: 'runtime.abortModal.mixing',
            })}
          </div>
        );
        break;
      }
    }
    const isExecutor = await dispatch(isProtocolExecutor());
    const userRoleLevel = availableRoleRights.map((e) => e.roleLevel)[0];
    const isRequester = usernameRequester === sessionState?.user?.username;
    const isUserCanAbortAnotherProtocol =
      userRoleLevel === UserRoleLevel.SERVICE ||
      userRoleLevel === UserRoleLevel.ADMINISTRATOR ||
      userRoleLevel === UserRoleLevel.TESSARA_SUPER_USER;

    if (!isExecutor && !(isUserCanAbortAnotherProtocol && isRequester)) {
      return;
    }

    const userName = isRequester ? sessionState?.user?.name : '';

    dispatch(
      showModal(
        intl.formatMessage({
          id: 'runtime.abortModal.title',
        }),
        abortText,
        dispatch(createAbortModalButtons(callback, userName)),
        () => {},
        'abortModal',
        true,
      ),
    );
  };

const isHeadCarryingTips = (pausedOn) =>
  pausedOn === LastExecEnum.ALIGN_TO_ASPIRATE ||
  pausedOn === LastExecEnum.ALIGN_TO_ASPIRATE_CARRY_LIQUID ||
  pausedOn === LastExecEnum.ALIGN_TO_ASPIRATE_CARRY_LIQUIDS ||
  pausedOn === LastExecEnum.ASPIRATE ||
  pausedOn === LastExecEnum.ASPIRATE_CARRY_LIQUID ||
  pausedOn === LastExecEnum.ALIGN_TO_DISPENSE ||
  pausedOn === LastExecEnum.ALIGN_TO_DISPENSE_CARRY_LIQUIDS ||
  pausedOn === LastExecEnum.DISPENSE_LIQUIDS ||
  pausedOn === LastExecEnum.ALIGN_TO_ASPIRATE_REUSE_TIP ||
  pausedOn === LastExecEnum.ALIGN_TO_RETURN_TIP ||
  pausedOn === LastExecEnum.RETURN_TIP ||
  pausedOn === LastExecEnum.DISPENSE_LIQUID ||
  pausedOn === LastExecEnum.ENTER_WELL_BEFORE_ASPIRATE ||
  pausedOn === LastExecEnum.USER_CONFIRMATION_CARRY_TIP ||
  pausedOn === LastExecEnum.MIXING;

export const showPostCollisionRecoveryConfirmationModal =
  (mustReleaseTip, pausedOnParam, runtimeStatusParam) => async (dispatch, getState) => {
    const {runtimeState} = getState();
    const {pausedOn, status, runtimeType} = runtimeState;

    const lastExecutedTask = pausedOnParam || pausedOn;
    const runtimeStatus = runtimeStatusParam || status;

    const isCarryingTip = isHeadCarryingTips(lastExecutedTask);
    const isPlungerMotorSkip = mustReleaseTip && isCarryingTip;
    const isMissingTrayError = runtimeStatus === RuntimeMode.ERROR_MISSING_TRAY;

    const isDisplayOptions = isCarryingTip && !isMissingTrayError && !isPlungerMotorSkip;

    let isTipsBroken = isPlungerMotorSkip;

    const onResumeProtocol = () => {
      console.debug('ResumeProtocol', isTipsBroken);
      dispatch(showLoading(intl.formatMessage({id: 'runtime.recoveryModal.loadingText'})));
      dispatch(resumeProtocol(isTipsBroken, true, true));

      if (runtimeType === RuntimeType.CONSOLIDATE_TIPS && isTipsBroken) {
        dispatch(setExecutionCount(1));
      }
    };

    const onAbortProtocol = () => {
      console.debug('AbortProtocol');
      dispatch(
        abortPauseResponseHandler(lastExecutedTask, () => {
          dispatch(resumeResponseHandler(mustReleaseTip));
        }),
      );
    };

    const title = intl.formatMessage({id: 'runtime.recoveryModal.title'});

    const bodyText = isMissingTrayError
      ? intl.formatMessage({id: 'runtime.recoveryModal.text.missingTray'})
      : intl.formatMessage({id: 'runtime.recoveryModal.text'});

    const optionItems = [
      {
        // index 0 / tips are fine
        content: <FormattedHTMLMessage id="runtime.recoveryModal.optionOne" />,
        hint: intl.formatMessage({id: 'runtime.recoveryModal.optionOne.hint'}),
        type: HintType.INFORMATION,
      },
      {
        // index 1 / tips are broken
        content: <FormattedHTMLMessage id="runtime.recoveryModal.optionTwo" />,
        hint: intl.formatMessage({id: 'runtime.recoveryModal.optionTwo.hint'}),
        type: HintType.INFORMATION,
      },
    ];

    const modalText = (
      <div>
        {bodyText}
        {isDisplayOptions && (
          <div>
            {intl.formatMessage({id: 'runtime.recoveryModal.text.withOptions'})}
            <FmlxRadioButton
              selectedIndex={Number(isTipsBroken)}
              onChange={(e) => {
                isTipsBroken = Boolean(e.index);
              }}
              items={optionItems}
            />
          </div>
        )}
      </div>
    );

    dispatch(
      showModalSimplified(title, modalText, [
        {
          onClick: onResumeProtocol,
          label: intl.formatMessage({id: CommonButtonLabel.CONTINUE}),
        },
        {
          onClick: onAbortProtocol,
          label: intl.formatMessage({id: CommonButtonLabel.ABORT_PROTOCOL}),
        },
      ]),
    );
  };

export const resumeResponseHandler =
  (mustReleaseTip = false) =>
  (dispatch, getState) => {
    const {runtimeState} = getState();
    const {status} = runtimeState;

    if (
      status === RuntimeMode.ERROR_ABORT_MOTOR_SKIP ||
      status === RuntimeMode.ERROR_ABORT_MOTOR_COLLISION_SKIP ||
      status === RuntimeMode.ERROR_MISSING_TRAY
    ) {
      dispatch(showPostCollisionRecoveryConfirmationModal(mustReleaseTip));
    } else {
      dispatch(resumeProtocol(false, false, true));
    }
  };

export function showDelayModal(seconds, message) {
  return (dispatch) => {
    let remainingSeconds = seconds;
    let timerInterval;
    function showModalFun() {
      if (remainingSeconds > 0) {
        const time = convertSecondToHoursMinuteSecond(remainingSeconds);
        dispatch(
          showModal(
            intl.formatMessage({
              id: 'modal.delay.title',
            }),
            <div className="modal_protocol_delay">
              <div className="user-message-block">
                <p className="timer">
                  {intl.formatMessage(
                    {
                      id: 'modal.delay.timer',
                    },
                    {
                      hours: addLeadingZeros(time.hour),
                      minutes: addLeadingZeros(time.minute),
                      seconds: addLeadingZeros(time.second),
                    },
                  )}
                </p>
                <p className="message-label">
                  {intl.formatMessage({
                    id: 'modal.delay.message',
                  })}
                  &nbsp;
                  <span>{message}</span>
                </p>
              </div>
              <p>
                {intl.formatMessage({
                  id: 'modal.delay.timerLabel',
                })}
              </p>
              <p>
                {intl.formatMessage({
                  id: 'modal.delay.text',
                })}
              </p>
            </div>,
            [
              {
                className: 'btn-lg',
                style: 'primary',
                handlerClick: async () => {
                  dispatch(hideModal());
                  remainingSeconds = 0;
                  await dispatch(resumeProtocol(false, false, true));
                },
                text: intl.formatMessage({
                  id: 'modal.delay.firstButton',
                }),
              },
              {
                className: 'btn-lg',
                style: 'secondary',
                handlerClick: () => {
                  dispatch(hideModal());
                  dispatch(abortActionBatch());
                },
                text: intl.formatMessage({
                  id: 'modal.delay.secondButton',
                }),
              },
            ],
            () => {},
            'modal_delay_user_confirmation',
            true,
            'lg',
          ),
        );
        remainingSeconds -= 1;
      } else {
        clearInterval(timerInterval);
      }
    }
    timerInterval = setInterval(showModalFun, 1000);
    dispatch(setTimerInterval(timerInterval));
  };
}

export function showUserConfirmationModal(messageUserConfirmation) {
  return (dispatch) => {
    dispatch(setRuntimePauseRemainingTime(true));
    dispatch(
      showModal(
        intl.formatMessage({
          id: 'modal.userConfirmation.title',
        }),
        <div className="modal_delay_user_confirmation">
          <p className="message-label">
            {intl.formatMessage({
              id: 'modal.userConfirmation.messageLabel',
            })}
            &nbsp;
            <span>{messageUserConfirmation}</span>
          </p>
          <p>
            {intl.formatMessage({
              id: 'modal.userConfirmation.text',
            })}
          </p>
        </div>,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: async () => {
              dispatch(hideModal());
              await dispatch(resumeProtocol(false, false, true));
              dispatch(setRuntimePauseRemainingTime(false));
            },
            text: intl.formatMessage({
              id: 'modal.userConfirmation.firstButton',
            }),
          },
        ],
        () => {},
        'modal_delay_user_confirmation',
        true,
        'lg',
      ),
    );
  };
}

export const showModalScanFailed = (errorCode) => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'modal.scanFailed.title',
      }),
      <FormattedHTMLMessage id="modal.scanFailed.text" />,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            batch(() => {
              dispatch(setPrevErrorCodeSetTipModal(errorCode));
              dispatch(setShowSetTipModal(true));
              dispatch(hideModal());
            });
          },
          text: intl.formatMessage({
            id: 'modal.scanFailed.firstButton',
          }),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(hideModal());
            dispatch(scanTips());
          },
          text: intl.formatMessage({
            id: 'modal.scanFailed.secondButton',
          }),
        },
        {
          className: 'btn-lg',
          style: 'tertiary',
          handlerClick: () => {
            dispatch(hideModal());
            dispatch(abortActionBatch());
          },
          text: intl.formatMessage({
            id: 'modal.scanFailed.thirdButton',
          }),
        },
      ],
      () => {},
      'scan-fail-modal',
      true,
    ),
  );
};

export const showModalUnReachableTipsNeedManualArragement = () => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'modal.unReachableTipsNeedManualArragement.title',
      }),
      <>
        <div className="error-detail-label">
          {intl.formatMessage({
            id: 'modal.unReachableTipsNeedManualArragement.errorDetail',
          })}
        </div>
        <FormattedHTMLMessage id="modal.unReachableTipsNeedManualArragement.text" />
      </>,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            batch(() => {
              dispatch(hideModal());
              dispatch(scanTips());
            });
          },
          text: intl.formatMessage({
            id: 'modal.unReachableTipsNeedManualArragement.firstButton',
          }),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(hideModal());
            dispatch(abortActionBatch());
          },
          text: intl.formatMessage({
            id: 'modal.unReachableTipsNeedManualArragement.secondButton',
          }),
        },
      ],
      () => {},
      'scan-fail-modal',
      true,
    ),
  );
};

export function showModalRunningOutTips(remainingTips) {
  return (dispatch, getState) => {
    const {
      sessionState,
      protocolState: {lastOpenedProtocol},
    } = getState();
    const {trayType: protocolTrayType} = lastOpenedProtocol ?? {};
    const {user} = sessionState;

    let caddyTips = 384;
    if (protocolTrayType === TrayType.DOUBLE_CADDY) {
      caddyTips = 768;
    } else if (protocolTrayType === TrayType.HIGH_VOLUME_CADDY) {
      caddyTips = 192;
    }

    dispatch(
      showModal(
        intl.formatMessage({
          id: 'instrumentControlHubBusiness.runtimeError.tipsNotEnoughMessageModal.title',
        }),
        <FormattedHTMLMessage
          id={
            remainingTips > caddyTips
              ? 'instrumentControlHubBusiness.runtimeError.tipsNotEnoughAndYouHaveNoChoiceMessageModal.message'
              : 'instrumentControlHubBusiness.runtimeError.tipsNotEnoughMessageModal.message'
          }
          values={{remainingTips}}
        />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(scanTips());
              dispatch(hideModal());
            },
            text: intl.formatMessage({
              id: 'instrumentControlHubBusiness.runtimeError.tipsNotEnoughMessageModal.firstButton',
            }),
          },
          {
            className: 'btn-lg',
            style: 'secondary',
            handlerClick: async () => {
              await dispatch(pauseProtocol(true, user.username || ''));
              await dispatch(hideModal());
              await dispatch(abortPauseResponseHandler(null, () => dispatch(showModalRunningOutTips(remainingTips))));
            },
            text: intl.formatMessage({
              id: 'instrumentControlHubBusiness.runtimeError.tipsNotEnoughMessageModal.secondButton',
            }),
          },
        ],
        () => {
          dispatch(hideModal());
        },
        '',
        true,
      ),
    );
  };
}

export const showRearrangeTipModal = () => (dispatch) => {
  const buttons = [
    {
      className: 'btn-lg',
      style: 'primary',
      handlerClick: () => {
        dispatch(resumeProtocol(false, false, true));
        dispatch(hideModal());
      },
      text: intl.formatMessage({
        id: 'modal.running.rearrangeTipModal.firstButton',
      }),
    },
  ];
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'modal.running.rearrangeTipModal.title',
      }),
      <div>
        {intl.formatMessage({
          id: 'modal.running.rearrangeTipModal.message',
        })}
      </div>,
      buttons,
      () => {},
      'modal-rearrange-tip',
      true,
    ),
  );
};

export const showTipsNotEnoughPartiallyModal = (requiredTips, availableTips) => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'instrumentControlHubBusiness.runtimeError.tipsNotEnoughPartially.title',
      }),
      <FormattedHTMLMessage
        id="instrumentControlHubBusiness.runtimeError.tipsNotEnoughPartially.message"
        values={{requiredTips, availableTips}}
      />,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            dispatch(resumeProtocol(false, false, true));
            dispatch(hideModal());
          },
          text: intl.formatMessage({
            id: 'instrumentControlHubBusiness.runtimeError.tipsNotEnoughPartially.firstButton',
          }),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(hideModal());
            dispatch(abortActionBatch());
          },
          text: intl.formatMessage({
            id: 'instrumentControlHubBusiness.runtimeError.tipsNotEnoughPartially.secondButton',
          }),
        },
      ],
      () => dispatch(hideModal()),
      'modal-tip-not-enough',
      true,
    ),
  );
};

export const showTipsNotEnoughPartiallyYouHaveNoChoiceModal = (requiredTips, availableTips) => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'instrumentControlHubBusiness.setProtocolTips.tipsNotEnoughPartiallyYouHaveNoChoiceModal.title',
      }),
      <FormattedHTMLMessage
        id="instrumentControlHubBusiness.setProtocolTips.tipsNotEnoughPartiallyYouHaveNoChoiceModal.message"
        values={{requiredTips, availableTips}}
      />,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            dispatch(resumeProtocol(false, false, true));
            dispatch(hideModal());
          },
          text: intl.formatMessage({
            id: 'instrumentControlHubBusiness.setProtocolTips.tipsNotEnoughPartiallyYouHaveNoChoiceModal.firstButton',
          }),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(hideModal());
            dispatch(abortActionBatch());
          },
          text: intl.formatMessage({
            id: 'instrumentControlHubBusiness.setProtocolTips.tipsNotEnoughPartiallyYouHaveNoChoiceModal.secondButton',
          }),
        },
      ],
      () => dispatch(hideModal()),
      'modal-tip-not-enough',
      true,
    ),
  );
};

export const showModalTipsNotEnoughPartiallyUnReachable = () => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'modal.tipsNotEnoughPartiallyUnReachable.title',
      }),
      <>
        <div className="error-detail-label">
          {intl.formatMessage({
            id: 'modal.tipsNotEnoughPartiallyUnReachable.errorDetail',
          })}
        </div>
        <FormattedHTMLMessage id="modal.tipsNotEnoughPartiallyUnReachable.text" />
      </>,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            batch(() => {
              dispatch(hideModal());
              dispatch(scanTips());
            });
          },
          text: intl.formatMessage({
            id: CommonButtonLabel.RETRY,
          }),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(hideModal());
            dispatch(abortActionBatch());
          },
          text: intl.formatMessage({
            id: CommonButtonLabel.ABORT,
          }),
        },
      ],
      () => {},
      'tipsNotEnoughPartiallyUnReachable-modal',
      true,
    ),
  );
};

export const showVisionErrorModal =
  (title, message, values = {}, isConfirm = false) =>
  (dispatch) => {
    const retryButton = {
      type: MessageBox.ButtonType.POSITIVE,
      text: intl.formatMessage({id: 'common.modal.action.retry'}),
      clickHandler: () => {
        dispatch(resumeProtocol(false, false, true));
        dispatch(hideModal());
        dispatch(
          showLoading(
            intl.formatMessage({
              id: 'runtimeBusiness.scanTip.loading',
            }),
          ),
        );
      },
    };
    const ignoreButton = {
      type: MessageBox.ButtonType.NEGATIVE,
      text: intl.formatMessage({id: 'common.modal.action.ignore'}),
      clickHandler: () => {
        dispatch(resumeProtocol(true, false, true));
        dispatch(hideModal());
        dispatch(
          showLoading(
            intl.formatMessage({
              id: 'runtimeBusiness.scanTip.loading',
            }),
          ),
        );
      },
    };

    const abortButton = {
      type: MessageBox.ButtonType.NEGATIVE,
      text: intl.formatMessage({id: 'common.modal.action.abort'}),
      clickHandler: () => dispatch(abortActionBatch()),
    };

    const confirmButton = {
      type: MessageBox.ButtonType.POSITIVE,
      text: intl.formatMessage({id: 'common.modal.action.gotIt'}),
      clickHandler: () => dispatch(hideModal()),
    };

    let buttons;

    if (isConfirm) {
      buttons = getMessageBoxActionButtons([confirmButton]);
    } else if (title === 'modal.visionDefaultError.title' || title.startsWith('modal.caddyNotFound')) {
      buttons = getMessageBoxActionButtons([retryButton, abortButton]);
    } else {
      buttons = getMessageBoxActionButtons([retryButton, ignoreButton, abortButton]);
    }

    dispatch(
      showModal(
        <FormattedHTMLMessage id={title} />,
        <FormattedHTMLMessage id={message} values={{...values}} />,
        buttons,
        () => dispatch(hideModal()),
        '',
        true,
        'md',
      ),
    );
  };

export const showRuntimeErrorBarcodeModal =
  (barcodeNotFound = [], unknownBarcode = []) =>
  (dispatch, getState) => {
    const {labwareState} = getState();

    const buttonGroup = [
      {
        type: MessageBox.ButtonType.POSITIVE,
        text: intl.formatMessage({id: 'common.modal.action.retry'}),
        clickHandler: () => {
          dispatch(resumeProtocol(false, false, true));
          dispatch(hideModal());
          dispatch(
            showLoading(
              intl.formatMessage({
                id: 'runtimeBusiness.scanTip.loading',
              }),
            ),
          );
        },
      },
      {
        type: MessageBox.ButtonType.NEGATIVE,
        text: intl.formatMessage({id: 'common.modal.action.ignore'}),
        clickHandler: () => {
          dispatch(resumeProtocol(true, false, true));
          dispatch(hideModal());
          dispatch(
            showLoading(
              intl.formatMessage({
                id: 'runtimeBusiness.scanTip.loading',
              }),
            ),
          );
        },
      },
      {
        type: MessageBox.ButtonType.NEGATIVE,
        text: intl.formatMessage({id: 'common.modal.action.abort'}),
        clickHandler: () => dispatch(abortActionBatch()),
      },
    ];

    const picklistLabwareIDs = dispatch(getPicklistLabwareIDs(true));

    const newLabwareState = {};

    Object.keys(labwareState).forEach((key) => {
      const labware = labwareState[key];
      if (key !== 2 && picklistLabwareIDs.includes(labware.LabwareID)) {
        newLabwareState[key] = {status: BarcodeStatus.VALID, barcode: labware?.barcode ?? labware?.name};
      } else if (Number(key)) {
        newLabwareState[key] = {};
      }
    });

    Object.keys(barcodeNotFound).forEach((idx) => {
      newLabwareState[barcodeNotFound[idx]?.position] = {
        status: BarcodeStatus.UNIDENTIFIED,
        barcode: barcodeNotFound[idx]?.barcode,
      };
    });

    Object.keys(unknownBarcode).forEach((idx) => {
      newLabwareState[unknownBarcode[idx]?.position] = {
        status: BarcodeStatus.UNKNOWN,
        barcode: unknownBarcode[idx]?.barcode,
      };
    });

    dispatch(
      showModal(
        <FormattedHTMLMessage id="modal.barcodeNotFound.title" />,
        <div>
          <ErrorBarcodeModalContent barcodeLabware={newLabwareState} />
          <FormattedHTMLMessage id="modal.barcodeNotFound.message" />
        </div>,
        getMessageBoxActionButtons(buttonGroup),
        () => dispatch(hideModal()),
        'barcode-error-modal',
        true,
      ),
    );
  };

export const showRuntimeMissingTrayModal = (callback) => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'runtime.missingTrayModal.title',
      }),
      <FormattedHTMLMessage id="runtime.missingTrayModal.text" />,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            batch(() => {
              dispatch(hideModal());
              callback();
            });
          },
          text: intl.formatMessage({
            id: 'runtime.missingTrayModal.firstButton',
          }),
        },
      ],
    ),
  );
};

export const showRunningProtocolWillTakeMoreTime = () => (dispatch) => {
  dispatch(
    showFormattedModal('runtime.runningTakeMoreTime', [
      {
        onClick: () => dispatch(resumeProtocol(false, false, true)),
        label: CommonButtonLabel.CONFIRM,
      },
      {
        onClick: () => dispatch(abortActionBatch()),
        label: CommonButtonLabel.ABORT,
      },
    ]),
  );
};

// #region runApiResponseHandler

export function showRuntimePlatePosSuggestionAvailableModal(payload) {
  return (dispatch, getState) => {
    const {labwareState} = getState();
    const newLabwareState = {
      1: {},
      2: {},
      3: {},
      4: {},
      5: {},
      6: {},
    };
    for (let iPayload = 0; iPayload < payload.length; iPayload += 1) {
      const {currentPosition, suggestedPosition} = payload[iPayload];
      newLabwareState[suggestedPosition.position] = labwareState[currentPosition.position];
    }
    dispatch(
      showModal(
        intl.formatMessage({
          id: 'modal.runtimePlatePosSuggestionAvailable.title',
        }),
        <div>
          <FormattedHTMLMessage id="modal.runtimePlatePosSuggestionAvailable.text" />
          <ErrorBarcodeModalContent barcodeLabware={newLabwareState} />
          <FormattedHTMLMessage id="modal.runtimePlatePosSuggestionAvailable.text2" />
        </div>,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              batch(() => {
                dispatch(setLabwareState(newLabwareState));
                dispatch(hideModal());
                dispatch(updateLabwareCurrentRunProtocol(newLabwareState));
                dispatch(resumeProtocol(false, false, true));
              });
            },
            text: intl.formatMessage({
              id: 'modal.runtimePlatePosSuggestionAvailable.firstButton',
            }),
          },
          {
            className: 'btn-lg',
            style: 'secondary',
            handlerClick: () => {
              dispatch(hideModal());
              dispatch(abortActionBatch());
            },
            text: intl.formatMessage({
              id: 'modal.runtimePlatePosSuggestionAvailable.secondButton',
            }),
          },
        ],
        () => {},
        'barcode-error-modal',
        true,
      ),
    );
  };
}

export const showRuntimeAbortedModal =
  (abortUsernameRequester = '') =>
  async (dispatch, getState) => {
    const isExecutor = await dispatch(isProtocolExecutor());
    if (!isExecutor) return;

    const {isTessaraProtocol} = getState().runtimeState || {};
    const isTessaraSuperUser = dispatch(isRoleLevel(UserRoleLevel.TESSARA_SUPER_USER));

    if (abortUsernameRequester) {
      dispatch(
        showRuntimeDialog(
          {
            type: Feedback.INFO,
            headerText: {
              id: 'runtimeNotification.abortedByAnotherUser.headerText',
              values: {userName: abortUsernameRequester},
            },
          },
          0,
          true,
        ),
      );
    } else {
      const msgId =
        isTessaraProtocol && !isTessaraSuperUser
          ? 'runtimeNotification.abortedNoOption.headerText'
          : 'runtimeNotification.aborted.headerText';

      dispatch(
        showRuntimeDialog({
          type: Feedback.ERROR,
          headerText: {
            id: msgId,
            values: {},
          },
        }),
      );
    }
  };

export const showRuntimePausedModal = () => async (dispatch) => {
  const isExecutor = await dispatch(isProtocolExecutor());
  if (!isExecutor) return;

  dispatch(
    showRuntimeDialog({
      type: Feedback.INFO,
      headerText: {
        id: 'runtimeNotification.paused.headerText',
        values: {},
      },
    }),
  );
};

export const showRuntimeFinishedModal = () => async (dispatch) => {
  const isExecutor = await dispatch(isProtocolExecutor());
  if (!isExecutor) return;

  dispatch(
    showRuntimeDialog({
      type: Feedback.SUCCESS,
      headerText: {
        id: 'runtimeNotification.finnishRunning',
        values: {},
      },
    }),
  );
};

export function showImpossibleLabwarePosModal() {
  return (dispatch) => {
    dispatch(
      showModal(
        intl.formatMessage({
          id: 'modal.impossibleLabwarePos.title',
        }),
        <FormattedHTMLMessage id="modal.impossibleLabwarePos.text" />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
            },
            text: intl.formatMessage({
              id: 'modal.impossibleLabwarePos.firstButton',
            }),
          },
        ],
        () => {
          dispatch(hideModal());
        },
        '',
        false,
      ),
    );
  };
}

export function showPlatePosSuggestionAvailableModal(payload) {
  return (dispatch, getState) => {
    dispatch(
      showModal(
        intl.formatMessage({
          id: 'modal.platePosSuggestionAvailable.title',
        }),
        <FormattedHTMLMessage id="modal.platePosSuggestionAvailable.text" />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              const {labwareState} = getState();
              dispatch(hideModal());

              batch(() => {
                const newLabwareState = {
                  1: {},
                  2: {},
                  3: {},
                  4: {},
                  5: {},
                  6: {},
                };
                for (let iPayload = 0; iPayload < payload.length; iPayload += 1) {
                  const {currentPosition, suggestedPosition} = payload[iPayload];
                  newLabwareState[suggestedPosition.position] = labwareState[currentPosition.position];
                }

                const originalCSVState = dispatch(convertCardStateToCsv());
                dispatch(addTemporaryState('CSVStateBeforeLabwareRearrangement', originalCSVState));

                dispatch(setLabwareState(newLabwareState));
                dispatch(saveProtocol());
              });
            },
            text: intl.formatMessage({
              id: 'modal.platePosSuggestionAvailable.firstButton',
            }),
          },
          {
            className: 'btn-lg',
            style: 'secondary',
            handlerClick: () => {
              dispatch(hideModal());
            },
            text: intl.formatMessage({
              id: 'modal.platePosSuggestionAvailable.secondButton',
            }),
          },
        ],
        () => {
          dispatch(hideModal());
        },
        '',
      ),
    );
  };
}

export function showInvalidASpirateDispenseVolumeSumModal() {
  return (dispatch) => {
    dispatch(
      showModal(
        intl.formatMessage({
          id: 'modal.improperAspirateDispenseVolumeSumModal.title',
        }),
        <FormattedHTMLMessage id="modal.improperAspirateDispenseVolumeSumModal.text" />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
            },
            text: intl.formatMessage({
              id: 'modal.improperAspirateDispenseVolumeSumModal.firstButton',
            }),
          },
        ],
        () => {
          dispatch(hideModal());
        },
        '',
        false,
      ),
    );
  };
}

export function showUnknownPlateId() {
  return (dispatch) => {
    const title = intl.formatMessage({id: 'modal.unknownPlateId.title'});
    const content = <FormattedHTMLMessage id="modal.unknownPlateId.text" />;
    const button = intl.formatMessage({id: 'modal.unknownPlateId.firstButton'});

    dispatch(
      showModal(
        title,
        content,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
            },
            text: button,
          },
        ],
        () => dispatch(hideModal()),
      ),
    );
  };
}

export function showModalHeadBrokenTips() {
  return (dispatch) => {
    dispatch(
      showModal(
        intl.formatMessage({
          id: 'modal.headBrokenTips.title',
        }),
        <FormattedHTMLMessage id="modal.headBrokenTips.text" />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
            },
            text: intl.formatMessage({
              id: 'modal.headBrokenTips.firstButton',
            }),
          },
        ],
        () => {
          dispatch(hideModal());
        },
        '',
        false,
      ),
    );
  };
}

export function showModalImposiblePickUpPosition(errCode, isTrayDoubleCaddy) {
  return (dispatch) => {
    let textIdSuffix = '';

    if (isTrayDoubleCaddy) {
      switch (errCode) {
        case FastRobotErrorcode.UNREACHABLE_TIPS_NEED_REMOVE_DECK_1:
          textIdSuffix = 'RemoveCaddyOnInsert1';
          break;
        case FastRobotErrorcode.UNREACHABLE_TIPS_NEED_REMOVE_DECK_1_NOT_ENOUGH_TIPS_DECK_2:
          textIdSuffix = 'RemoveCaddyOnInsert1NotEnoughTips';
          break;
        case FastRobotErrorcode.UNREACHABLE_TIPS_NEED_MOVE_CADDY_TO_OTHER_DECK:
          textIdSuffix = 'MoveCaddytoInsert2';
          break;
        case FastRobotErrorcode.UNREACHABLE_TIPS_NEED_MANUAL_ARRAGEMENT:
          textIdSuffix = 'ChangetoFullCaddy';
          break;
        default:
          break;
      }
    }

    dispatch(
      showModal(
        intl.formatMessage({
          id: 'modal.impossibleTipPickUpPosition.title',
        }),
        <FormattedHTMLMessage id={`modal.impossibleTipPickUpPosition${textIdSuffix}.text`} />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
              dispatch(scanTips());
            },
            text: intl.formatMessage({
              id: 'modal.impossibleTipPickUpPosition.firstButton',
            }),
          },
          {
            className: 'btn-lg',
            style: 'secondary',
            handlerClick: () => {
              dispatch(hideModal());
              dispatch(abortActionBatch());
            },
            text: intl.formatMessage({
              id: 'modal.impossibleTipPickUpPosition.secondButton',
            }),
          },
        ],
        () => {},
        '',
        true,
      ),
    );
  };
}

export function showModalUnsupportedTipPickUpPosition() {
  return (dispatch) => {
    dispatch(
      showModal(
        intl.formatMessage({
          id: 'modal.unsupportedTipPickUpPosition.title',
        }),
        <FormattedHTMLMessage id="modal.unsupportedTipPickUpPosition.text" />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
              dispatch(scanTips());
            },
            text: intl.formatMessage({
              id: 'modal.unsupportedTipPickUpPosition.firstButton',
            }),
          },
          {
            className: 'btn-lg',
            style: 'secondary',
            handlerClick: () => {
              dispatch(hideModal());
              dispatch(abortActionBatch());
            },
            text: intl.formatMessage({
              id: 'modal.unsupportedTipPickUpPosition.secondButton',
            }),
          },
        ],
        () => {
          dispatch(hideModal());
        },
        '',
        false,
      ),
    );
  };
}
// #endregion runApiResponseHandler

export function showImpossibleConsolidateTipsModal(consolidateTipsStatus) {
  return (dispatch) => {
    let title = '';
    let message = '';
    switch (consolidateTipsStatus) {
      // eslint-disable-next-line default-case-last
      default:
      case LastExecEnum.CONSOLIDATE_TIPS_EMPTY_SOURCE:
        title = intl.formatMessage({id: 'modal.impossibleConsolidateTips.title.insufficientTipsCaddy1'});
        message = intl.formatMessage({id: 'modal.impossibleConsolidateTips.message.insufficientTipsCaddy1'});
        break;
      case LastExecEnum.CONSOLIDATE_TIPS_FULL_DESTINATION:
        title = intl.formatMessage({id: 'modal.impossibleConsolidateTips.title.insufficientSpaceCaddy2'});
        message = intl.formatMessage({id: 'modal.impossibleConsolidateTips.message.insufficientSpaceCaddy2'});
        break;
    }

    const defaultHandler = async () => {
      dispatch(hideModal());
      await dispatch(resumeProtocol(false, false, true));
    };

    const buttonAction = [
      {
        className: 'btn-lg',
        style: 'primary',
        handlerClick: async () => {
          await defaultHandler();
          dispatch(setExecutionCount(1));
        },
        text: intl.formatMessage({id: 'modal.impossibleConsolidateTips.continueButton'}),
      },
      {
        className: 'btn-lg',
        style: 'secondary',
        handlerClick: async () => {
          await defaultHandler();
        },
        text: intl.formatMessage({id: 'modal.impossibleConsolidateTips.finishButton'}),
      },
    ];

    dispatch(
      showModal(
        title,
        message,
        buttonAction,
        () => {
          dispatch(hideModal());
        },
        '',
        true,
        'md',
      ),
    );
  };
}

export const showInvalidCaddyConditionConsolidateTips = (err) => (dispatch) => {
  const modalTitle = parseMessage('modal.consolidateTips.invalidCaddy.title');
  let modalText;

  switch (err) {
    case FastRobotErrorcode.CONSOLIDATE_TIPS_TASK_NO_CADDY_1:
      modalText = parseMessage('modal.consolidateTips.invalidCaddy.caddy1Empty');
      break;
    case FastRobotErrorcode.CONSOLIDATE_TIPS_TASK_NO_CADDY_2:
      modalText = parseMessage('modal.consolidateTips.invalidCaddy.caddy2Empty');
      break;
    default:
      modalText = parseMessage('modal.consolidateTips.invalidCaddy.noCaddies');
      break;
  }

  const buttons = [
    {
      text: parseMessage(CommonButtonLabel.RETRY),
      handlerClick: () => {
        dispatch(resumeProtocol(false, false, true));
        if (err !== FastRobotErrorcode.CADDY_NOT_FOUND) {
          dispatch(setExecutionCount(1));
        }
      },
    },
    {
      text: parseMessage(CommonButtonLabel.ABORT),
      handlerClick: () => dispatch(abortActionBatch()),
    },
  ];

  dispatch(showModal(modalTitle, modalText, buttons));
};

export const showAbnormalYawDetectedModal = () => (dispatch) => {
  const title = parseMessage('modal.detectsHardwareChange.V2.title');
  const text = parseMessage('modal.detectsHardwareChange.V2.text');
  const buttons = [
    {
      label: parseMessage('modal.detectsHardwareChange.V2.firstButton'),
      onClick: () => dispatch(openTipsPickupCalibrationMenu()),
    },
    {
      label: parseMessage(CommonButtonLabel.ABORT),
      onClick: () => dispatch(abortActionBatch()),
    },
  ];

  dispatch(showModalSimplified(title, text, buttons));
};

export const showModalNeedInsertEmptyWasteCaddy = () => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'modal.needInsertEmptyWasteCaddy.title',
      }),
      <FormattedHTMLMessage id="modal.needInsertEmptyWasteCaddy.text" />,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            batch(() => {
              dispatch(hideModal());
              dispatch(resumeProtocol());
            });
          },
          text: intl.formatMessage({
            id: CommonButtonLabel.RETRY,
          }),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(hideModal());
            dispatch(abortActionBatch());
          },
          text: intl.formatMessage({
            id: CommonButtonLabel.ABORT,
          }),
        },
      ],
      () => {},
      'needInsertEmptyWasteCaddy-modal',
      true,
    ),
  );
};

export const showModalNeedEmptyWasteCaddyAndRefillTipCaddy = () => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'modal.needEmptyWasteCaddyAndRefillTipCaddy.title',
      }),
      <>
        <FormattedHTMLMessage id="modal.needEmptyWasteCaddyAndRefillTipCaddy.text" />
        <ul>
          <li>
            <FormattedHTMLMessage id="modal.needEmptyWasteCaddyAndRefillTipCaddy.detailsText1" />
          </li>
          <li>
            <FormattedHTMLMessage id="modal.needEmptyWasteCaddyAndRefillTipCaddy.detailsText2" />
          </li>
        </ul>
      </>,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            batch(() => {
              dispatch(hideModal());
              dispatch(resumeProtocol());
            });
          },
          text: intl.formatMessage({
            id: CommonButtonLabel.CONTINUE,
          }),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(hideModal());
            dispatch(abortActionBatch());
          },
          text: intl.formatMessage({
            id: CommonButtonLabel.ABORT,
          }),
        },
      ],
      () => {},
      'needEmptyWasteCaddyAndRefillTipCaddy-modal',
      true,
    ),
  );
};

export const showModalWasteCaddyEjectPositionNotAvailable = () => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'modal.wasteCaddyEjectPositionNotAvailable.title',
      }),
      <FormattedHTMLMessage id="modal.wasteCaddyEjectPositionNotAvailable.text" />,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            batch(() => {
              dispatch(hideModal());
              dispatch(resumeProtocol());
            });
          },
          text: intl.formatMessage({
            id: CommonButtonLabel.RETRY,
          }),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(hideModal());
            dispatch(abortActionBatch());
          },
          text: intl.formatMessage({
            id: CommonButtonLabel.ABORT,
          }),
        },
      ],
      () => {},
      'wasteCaddyEjectPositionNotAvailable-modal',
      true,
    ),
  );
};

export const showModalPickTipCaddyCollideWithWasteCaddy = () => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'modal.pickTipCaddyCollideWithWasteCaddy.title',
      }),
      <FormattedHTMLMessage id="modal.pickTipCaddyCollideWithWasteCaddy.text" />,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            batch(() => {
              dispatch(hideModal());
              dispatch(resumeProtocol());
            });
          },
          text: intl.formatMessage({
            id: CommonButtonLabel.RETRY,
          }),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(hideModal());
            dispatch(abortActionBatch());
          },
          text: intl.formatMessage({
            id: CommonButtonLabel.ABORT,
          }),
        },
      ],
      () => {},
      'pickTipCaddyCollideWithWasteCaddy-modal',
      true,
    ),
  );
};

export const showModalEjectToCaddyFallenTipsDetected = () => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'modal.ejectToCaddyFallenTipsDetected.title',
      }),
      <FormattedHTMLMessage id="modal.ejectToCaddyFallenTipsDetected.text" />,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            batch(() => {
              dispatch(hideModal());
              dispatch(resumeProtocol());
            });
          },
          text: intl.formatMessage({
            id: CommonButtonLabel.RETRY,
          }),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(hideModal());
            dispatch(abortActionBatch());
          },
          text: intl.formatMessage({
            id: CommonButtonLabel.ABORT,
          }),
        },
      ],
      () => {},
      'ejectToCaddyFallenTipsDetected-modal',
      true,
    ),
  );
};

export function showUnsupportedLabwareModal() {
  return (dispatch) => {
    dispatch(
      showModal(
        intl.formatMessage({
          id: 'modal.unsupportedLabware.title',
        }),
        <FormattedHTMLMessage id="modal.unsupportedLabware.text" />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
            },
            text: intl.formatMessage({
              id: 'modal.unsupportedLabware.firstButton',
            }),
          },
        ],
        () => {
          dispatch(hideModal());
        },
        '',
        false,
      ),
    );
  };
}
